import { Component, EventEmitter, Input, OnDestroy, OnInit, Output, ViewContainerRef, ViewEncapsulation } from '@angular/core';
import { Subject, Subscription } from 'rxjs';
import { debounceTime, distinctUntilChanged } from 'rxjs/operators';

import { Api } from '../../../api/api';
import { environment } from '../../../environment';
import { ESRI } from '../../../esri-loader-util';
import { InputConverter, JSONConverter } from '../../../inputConverter';
import { DistanceUnits, FillStyleOptions, LineStyleOptions } from '../../../selectionOptions';
import { BufferService } from '../../../services/buffer.service';
import { LayerService } from '../../../services/layer.service';
import { MapCoreLayer } from '../../../types';
import { BaseComponent } from '../../base.component';

let SketchViewModel: __esri.SketchViewModelConstructor;
let FeatureLayer: __esri.FeatureLayerConstructor;
let Query: __esri.QueryConstructor;
let GeometryService: __esri.GeometryServiceConstructor;

@Component({
    encapsulation: ViewEncapsulation.ShadowDom,
    selector: 'mc-draw-intersection',
    templateUrl: './draw-intersection.component.html',
    styleUrls: ['../../sharedStyles/base.scss', './draw-intersection.component.scss']
})
export class DrawIntersectionComponent extends BaseComponent implements OnInit, OnDestroy {
    @Input()
    set enabled(enabled: boolean) {
        this._enabled = enabled;
        this._enabled ? this.activate() : this.deactivate();
    }
    _enabled: boolean;

    @Input()
    @InputConverter(JSONConverter)
    defaultSymbolProperties = {
        type: 'simple-marker', // autocasts as SimpleMarkerSymbol
        style: 'circle',
        name: 'Circle',
        color: 'rgba(255, 255, 255, 0.5)',
        size: 12,
        outline: { // autocasts as SimpleLineSymbol
            color: 'black',
            width: 1
        }
    };

    @Input() debounceTimer: number = 1000;
    private readonly intersectionClicks = new Subject<__esri.SketchViewModelCreateEvent>();
    private intersectionClicksSub: Subscription;

    bufferDistanceUnits;

    geometryService: __esri.GeometryService;

    @Output()
    public readonly drawComplete: EventEmitter<__esri.Graphic> = new EventEmitter();

    constructor(_vcr: ViewContainerRef,
        private readonly layerService: LayerService,
        private readonly bufferService: BufferService,
        private readonly api: Api) {
        super(_vcr);
    }

    sketchViewModel: __esri.SketchViewModel;
    sketchHandler;
    mapView: NonNullable<__esri.MapView>;
    snapLayer: NonNullable<__esri.GraphicsLayer>;
    stateLayer: MapCoreLayer;
    localLayer: MapCoreLayer;
    selectedRoadLayer: MapCoreLayer;
    intersectionType: IntersectionType = 'circle';
    intersectionName: string;
    crossBufferOptions: ICrossBufferOptions = {
        bufferDistance: 10,
        bufferDistanceUnit: 'feet',
        intersectionDistance: 100,
        intersectionDistanceUnit: 'feet'
    };

    bufferOptions = {
        distance: this.crossBufferOptions.intersectionDistance,
        distanceUnit: this.crossBufferOptions.intersectionDistanceUnit,
        fillType: FillStyleOptions[0].value,
        fillColor: '#ff0000',
        outlineType: LineStyleOptions[0].value,
        outlineColor: '#ff0000',
        lineWidth: 2,
        opacity: 0.5
    };

    async ngOnInit() {
        try {
            this.mapView = await this.setupMapViewRef(this.mapView);
            this.bufferDistanceUnits = DistanceUnits;
            [SketchViewModel, FeatureLayer, Query, GeometryService] = await Promise.all([ESRI.SketchViewModel, ESRI.FeatureLayer, ESRI.Query, ESRI.GeometryService]);
            this.stateLayer = new FeatureLayer({
                url: `${environment.apiBaseUrl}/arcgis/paroads/1`,
                id: 'stateLayer',
                outFields: ['ST_RT_NO', 'STREET_NAME', 'OBJECTID', 'SEG_NO', 'SEG_LNGTH_FEET', 'CTY_CODE', 'JURIS', 'SIDE_IND'],
                opacity: 0.7,
                minScale: 25000,
                listMode: 'hide',
                visible: false
            }) as MapCoreLayer;
            this.localLayer = new FeatureLayer({
                url: `${environment.apiBaseUrl}/arcgis/paroads/0`,
                id: 'localLayer',
                outFields: ['LR_RT_NO', 'LR_NAME', 'LR_TYPE'],
                opacity: 0.7,
                minScale: 25000,
                listMode: 'hide',
                visible: false
            }) as MapCoreLayer;
            this.selectedRoadLayer = new FeatureLayer({
                url: `${environment.apiBaseUrl}/arcgis/paroads/2`,
                id: 'selectedRoadLayer',
                outFields: ['OBJECTID', 'GEOMETRY'],
                opacity: 0.7,
                minScale: 25000,
                listMode: 'hide',
                visible: false
            }) as MapCoreLayer;

            this.stateLayer.mapcoreProps = { hideInLegend: true };
            this.localLayer.mapcoreProps = { hideInLegend: true };
            this.selectedRoadLayer.mapcoreProps = { hideInLegend: true };
            this.snapLayer = await this.layerService.addGraphicsLayer({ title: 'Intersection', hideInLegend: true, listMode: 'hide' });

        } catch (error) {
            this.readyProblem(error);
        } finally {
            this.isReady();
        }
    }

    ngOnDestroy(): void {
        this.deactivate();
    }

    private activate() {
        this.stateLayer.visible = true;
        this.localLayer.visible = true;
        this.selectedRoadLayer.visible = true;
        this.mapView.map.addMany([this.stateLayer, this.localLayer, this.selectedRoadLayer, this.snapLayer]);

        this.layerService.moveLayerToTheTop(this.stateLayer);
        this.layerService.moveLayerToTheTop(this.localLayer);
        this.layerService.moveLayerToTheTop(this.snapLayer);

        this.intersectionClicksSub = this.intersectionClicks.pipe(
            debounceTime(this.debounceTimer),
            distinctUntilChanged()
        ).subscribe((event: any) => {
            if (event.state === 'complete') {
                const graphic = event.graphic;
                this.addGraphic(graphic);
                this.createDrawAction();
            }
        });
        this.setSketchViewModel();
        this.createDrawAction();
    }

    private deactivate() {
        this.intersectionClicksSub.unsubscribe();
        this.mapView.map.removeMany([this.stateLayer, this.localLayer, this.selectedRoadLayer, this.snapLayer]);
        this.sketchViewModel.cancel();
        if (this.sketchHandler) {
            this.sketchHandler.remove();
        }
    }

    async createDrawAction() {
        if (this.sketchHandler) {
            this.sketchHandler.remove();
        }
        this.sketchViewModel.create('point');
        this.sketchHandler = this.sketchViewModel.on('create', (event) => {
            this.intersectionClicks.next(event);
        });
    }

    private async addGraphic(graphic: __esri.Graphic) {
        const nameList = await this.setIntersectionName(graphic.geometry);
        if (nameList.size === 0) {
            this.snapLayer.removeAll();
            return;
        }

        const intersectionLayer = await this.layerService.addGraphicsLayer({ title: this.intersectionName, graphicLayer: 'polygon' });
        intersectionLayer.add(graphic);

        if (this.intersectionType === 'cross') {
            await this.addIntersectionCross(graphic);
        } else {
            await this.addIntersectionCircle(graphic);
        }
        this.drawComplete.emit(graphic);
    }

    private async addIntersectionCircle(graphic: __esri.Graphic) {
        const point = graphic.geometry as __esri.Point;
        const circleOptions = { ...this.bufferOptions, distance: this.crossBufferOptions.intersectionDistance, distanceUnit: this.crossBufferOptions.intersectionDistanceUnit };
        const circleBuffer = await this.bufferService.getGeometryBuffers(point, circleOptions);
        const title = `${this.intersectionName} - Buffer (${this.crossBufferOptions.intersectionDistance} ${this.crossBufferOptions.intersectionDistanceUnit})`;
        const bufferLayer = await this.layerService.addGraphicsLayer({ title });
        bufferLayer.addMany(circleBuffer.map(b => b.graphic));
    }

    public async addIntersectionCross(graphic: __esri.Graphic) {
        let intersectionGeometries = [];
        const point = graphic.geometry as __esri.Point;
        const objectIds = await this.api.http2.post('mapLayer/get-offset-by-lat-long', {
            latitude: point.latitude,
            longitude: point.longitude,
            bufferDistance: this.crossBufferOptions.intersectionDistance
        }, { responseType: 'text' });

        const query = new Query();
        query.where = `OBJECTID in (${objectIds})`;
        query.returnGeometry = true;
        const selectedFeatures = await (this.selectedRoadLayer as __esri.FeatureLayer).queryFeatures(query);
        if (selectedFeatures && selectedFeatures.features) {
            intersectionGeometries = intersectionGeometries.concat(selectedFeatures.features.map(f => f.geometry));
        }
        const pointBuffer = await this.bufferService.getGeometryBuffers(point, this.bufferOptions);

        const localView = await this.mapView.whenLayerView(this.localLayer as __esri.FeatureLayer);
        const local = await localView.queryFeatures({
            geometry: graphic.geometry,
            distance: 10,
            units: 'feet',
            returnGeometry: true
        });

        if (!this.geometryService) {
            this.geometryService = new GeometryService({ url: environment.geometryServiceUrl });
        }

        if (local.features && local.features.length > 0) {
            const localRoadGeometries = await this.geometryService.intersect(local.features.map(f => f.geometry), pointBuffer[0].graphic.geometry);
            intersectionGeometries = intersectionGeometries.concat(localRoadGeometries);
        }

        const crossOptions = {
            ...this.bufferOptions,
            distance: this.crossBufferOptions.bufferDistance,
            distanceUnit: this.crossBufferOptions.bufferDistanceUnit
        };
        const intersectionBuffer = await this.bufferService.getGeometryBuffers(intersectionGeometries, crossOptions);

        const title = `${this.intersectionName} - Buffer (${this.crossBufferOptions.intersectionDistance} ${this.crossBufferOptions.intersectionDistanceUnit})`;
        const bufferLayer = await this.layerService.addGraphicsLayer({ title });
        bufferLayer.addMany(intersectionBuffer.map(b => b.graphic));
    }

    private async setIntersectionName(geometry: __esri.Geometry) {
        const query = new Query({
            geometry,
            distance: 10,
            units: 'feet',
            returnGeometry: true
        });

        const nameList = new Set();
        const localView = await this.mapView.whenLayerView(this.localLayer as __esri.FeatureLayer);
        const local = await localView.queryFeatures(query);
        local.features.forEach(feature => {
            if (feature.attributes.LR_NAME !== null) {
                nameList.add(`${feature.attributes.LR_NAME} ${feature.attributes.LR_TYPE}`);
            }
        });

        const stateView = await this.mapView.whenLayerView(this.stateLayer as __esri.FeatureLayer);
        const state = await stateView.queryFeatures(query);
        state.features.forEach(feature => {
            if (feature.attributes.STREET_NAME !== null) {
                nameList.add(feature.attributes.STREET_NAME);
            }
        });

        this.intersectionName = Array.from(nameList).join(' x ');
        return nameList;
    }

    private setSketchViewModel() {
        if (!this.sketchViewModel) {
            this.sketchViewModel = new SketchViewModel({
                view: this.mapView,
                layer: this.snapLayer,
                pointSymbol: this.defaultSymbolProperties as any,
                snappingOptions: {
                    enabled: true,
                    distance: 30,
                    featureSources: [
                        { layer: this.localLayer } as __esri.FeatureSnappingLayerSource,
                        { layer: this.stateLayer } as __esri.FeatureSnappingLayerSource
                    ]
                }
            });
        }
    }
}

type IntersectionType = 'circle' | 'cross';
interface ICrossBufferOptions {
    bufferDistance: number;
    bufferDistanceUnit: __esri.LinearUnits;
    intersectionDistance: number;
    intersectionDistanceUnit: __esri.LinearUnits;
}
